import { Icon, IconButton, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import {
  ArrowDropDownRounded,
  AutoFixHighRounded,
  EastRounded
} from "@mui/icons-material";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import {
  removeDestinationLanguage,
  setDestinationLanguage,
  setSourceLanguage,
  updateDestinationLanguage
} from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import { LanguageDataMap, LANGUAGES } from "@/constants/languages";
import { LanguageSelector } from "../../..";
import { MurfInputLabel } from "@/components/elements/MurfInputLabel";
import { UserAvatar } from "@/components/elements";
import { getLanguageFlag } from "@/utils/links";
import { pluralize } from "@/utils/misc";
import { CREATE_PROJECT_COMPACT_VIEW_WIDTH } from "@/features/createProject/constants";

export const RequirementsLanguageSelector = ({
  excludeLanguage,
  sourceLanguage,
  disableSourceLanguage,
  qcEnabled
}: {
  excludeLanguage?: LANGUAGES[];
  sourceLanguage?: LANGUAGES | null;
  disableSourceLanguage?: boolean;
  qcEnabled?: boolean;
}): React.ReactNode => {
  const dispatch = useAppDispatch();

  const translateToLanguages = useTypedSelector(
    (state) => state.clientProjectPopup.targetLocales || []
  );

  const translateFromLanguage = useTypedSelector(
    (state) => state.clientProjectPopup.sourceLocale
  );

  const selectedSourceLanguage =
    sourceLanguage || translateFromLanguage || LANGUAGES.AUTO;

  const supportedSourceLocales = useTypedSelector(
    (state) =>
      state.user?.activeWorkspaceData?.supportedSourceLocales?.map(
        (locale) => locale.locale
      ) || []
  );

  const supportedNonQCTargetLocales = useTypedSelector(
    (state) =>
      state.user?.activeWorkspaceData?.supportedNonQCTargetLocales?.map(
        (locale) => locale.locale
      ) || []
  );

  const supportedQCTargetLocales = useTypedSelector(
    (state) =>
      state.user?.activeWorkspaceData?.supportedQCTargetLocales?.map(
        (locale) => locale.locale
      ) || []
  );

  const supportedTargetLocales = useMemo(() => {
    if (qcEnabled) {
      return supportedQCTargetLocales;
    }
    return supportedNonQCTargetLocales;
  }, [qcEnabled, supportedNonQCTargetLocales, supportedQCTargetLocales]);

  const _setTranslateToLanguages = (language: string) => {
    if (translateToLanguages.includes(language as any)) {
      dispatch(removeDestinationLanguage(language));
      return;
    }
    dispatch(updateDestinationLanguage(language));
  };

  useEffect(() => {
    if (!translateFromLanguage) {
      dispatch(setSourceLanguage(sourceLanguage || LANGUAGES.AUTO));
    }
  }, [translateFromLanguage, dispatch, sourceLanguage]);

  return (
    <Stack
      borderRadius={2}
      direction={"row"}
      gap={2}
      // {...wrapperStyles}
    >
      <Stack flexGrow={1} flexShrink={0} width={`calc(50% - 32px)`}>
        <Stack gap={1}>
          <MurfInputLabel sx={{ flexShrink: 0 }}>
            Source Language
          </MurfInputLabel>
          <LanguageSelector
            optionsList={supportedSourceLocales as LANGUAGES[]}
            disable={disableSourceLanguage}
            onClick={(locale) => {
              dispatch(setSourceLanguage(locale));
            }}
            defaultSelected={selectedSourceLanguage}
            selectorElement={
              <Stack
                py={1}
                gap={1}
                direction={"row"}
                width={"100%"}
                alignItems={"center"}
                sx={(theme) => ({
                  [theme.breakpoints.down(CREATE_PROJECT_COMPACT_VIEW_WIDTH)]: {
                    minHeight: 38
                  },
                  [theme.breakpoints.up(CREATE_PROJECT_COMPACT_VIEW_WIDTH)]: {
                    minHeight: 54
                  }
                })}
              >
                {translateFromLanguage && translateFromLanguage !== "auto" ? (
                  <UserAvatar
                    src={getLanguageFlag({
                      language: translateFromLanguage
                        ? LanguageDataMap[translateFromLanguage]?.flag ??
                          "globe.svg"
                        : "globe.svg"
                    })}
                    displayName=""
                    alt={translateFromLanguage || "globe"}
                    sizes="24"
                    sx={{
                      height: 24,
                      width: 24,
                      flexShrink: 0
                    }}
                  />
                ) : (
                  <Icon sx={{ height: 24, width: 24 }}>
                    <AutoFixHighRounded sx={{ height: 24, width: 24 }} />
                  </Icon>
                )}

                <Typography
                  sx={{
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "24px" /* 150% */,
                    letterSpacing: "0.15px",
                    flexGrow: 1,
                    width: "100%",
                    textAlign: "left"
                  }}
                >
                  {translateFromLanguage && translateFromLanguage !== "auto"
                    ? LanguageDataMap[translateFromLanguage].name
                    : "Auto-Detect"}
                </Typography>
                <IconButton
                  sx={{
                    height: 24,
                    width: 24,
                    flexShrink: 0
                  }}
                >
                  <ArrowDropDownRounded />
                </IconButton>
              </Stack>
            }
            selectorElementProps={{
              color: "secondary",
              sx: {
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: (theme) => theme.palette.secondary.outlinedBorder,
                borderRadius: 2,
                p: (t) => t.spacing(0, 1.5),
                flex: "1 1 0",
                justifyContent: "start"
              },
              disabled: disableSourceLanguage
            }}
          />
        </Stack>
      </Stack>
      <EastRounded
        sx={(theme) => ({
          height: 24,
          width: 24,
          flexShrink: 0,
          [theme.breakpoints.down(CREATE_PROJECT_COMPACT_VIEW_WIDTH)]: {
            marginTop: "28px"
          },
          [theme.breakpoints.up(CREATE_PROJECT_COMPACT_VIEW_WIDTH)]: {
            marginTop: "36px"
          }
        })}
      />
      <Stack gap={1} flexGrow={1} width={`calc(50% - 32px)`}>
        <Stack gap={1}>
          <MurfInputLabel sx={{ flexShrink: 0 }}>
            {`
              Destination Languages (${pluralize(
                translateToLanguages?.length || 0,
                "language"
              )} selected)`}
          </MurfInputLabel>
          <LanguageSelector
            optionsList={
              supportedTargetLocales?.filter(
                (l) => !excludeLanguage?.includes(l as LANGUAGES)
              ) as LANGUAGES[]
            }
            onClick={(locale) => {
              _setTranslateToLanguages(locale);
            }}
            onMultiSelect={(locales: LANGUAGES[]) => {
              dispatch(setDestinationLanguage(locales));
            }}
            exclude={translateFromLanguage ? [translateFromLanguage] : []}
            selectedItems={translateToLanguages as any}
            multiSelect
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
